// "Start a jam" on a seed: one button, in the shapes the places that offer it
// need. Remixing is the seed page's main way out, so there it is the quieter
// outlined sibling under the remix row; on the jam picker it is the only
// action a card has, so it is filled.

import { Loader2, Users } from "lucide-react";
import { cn } from "@web-shared/lib/utils";
import { useStartJam } from "../hooks/useStartJam";

/**
 * `hero` sits under the seed page's remix row, `pill` in its phone layout,
 * `chip` along a feed card's footer and `row` on a phone's feed row (the jam
 * picker), `bar` in a room header, `dock` in the seed page's bottom bar on a
 * phone, beside Remix and Play here.
 */
export type StartJamSize = "hero" | "pill" | "chip" | "row" | "bar" | "dock";

const SHAPE: Record<StartJamSize, string> = {
  hero: "h-11 px-5 rounded-xl text-sm gap-2 border border-primary/40 text-primary hover:bg-primary/10",
  pill: "h-10 px-5 rounded-full text-sm gap-2 border border-primary/40 text-primary active:bg-primary/10",
  chip: "h-8 px-3.5 rounded-lg text-xs gap-1.5 bg-primary text-primary-foreground hover:bg-white shadow-glow",
  row: "h-8 px-3 rounded-full text-xs gap-1.5 bg-primary text-primary-foreground shrink-0",
  bar: "h-9 px-3 rounded-lg text-xs gap-1.5 border border-primary/40 text-primary hover:bg-primary/10",
  dock: "h-10 px-3 rounded-xl text-xs gap-1.5 border border-primary/40 text-primary active:bg-primary/10",
};

const ICON: Record<StartJamSize, string> = { hero: "size-4", pill: "size-4", chip: "size-3.5", row: "size-3.5", bar: "size-4", dock: "size-3.5" };

export function StartJamButton({
  seedId,
  size = "hero",
  label = "Start a jam",
  className,
  onBeforeStart,
}: {
  seedId: string;
  size?: StartJamSize;
  label?: string;
  className?: string;
  /** Runs first; false to stay (a room with unsaved changes asks). */
  onBeforeStart?: () => Promise<boolean>;
}) {
  const { start, startingId, error } = useStartJam();
  const busy = startingId === seedId;
  const Icon = busy ? Loader2 : Users;
  return (
    <span className={cn("inline-flex flex-col items-center", className)}>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          if (!onBeforeStart) return start(seedId);
          void onBeforeStart().then((go) => go && start(seedId));
        }}
        title={error ?? "Open a live room on this seed — anyone with the link can join"}
        className={cn(
          "inline-flex cursor-pointer items-center justify-center whitespace-nowrap font-bold transition-all disabled:cursor-wait disabled:opacity-70",
          SHAPE[size],
        )}
      >
        <Icon className={cn(ICON[size], busy && "animate-spin")} />
        {busy ? "Starting…" : label}
      </button>
      {error && size !== "chip" && size !== "row" && (
        <span role="alert" className="mt-1.5 text-xs text-removed">{error}</span>
      )}
    </span>
  );
}
